import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import { useState } from 'react';
import { useCreateEpdDataQualityAssessmentSummary, useUpdateEpdDataQualityAssessmentSummary } from 'services/api/mutations';
import { useGetEpdDataQualityAssessment } from 'services/api/queries';
import { DataQualityAssessmentSummaryModel } from 'types/types';

import DataQualityAssessmentAndReferenceYearsGrid from '../../grids/data-quality-assessment-and-ref-years/DataQualityAssessmentAndReferenceYearsGrid';
import MaximizedDialog from '../expandable/MaximizedDialog';
import usePcrV2 from '../../hooks/usePcrV2';

type TProps = {
  epdVersionId: string;
  errors?: any;
};

type LabelsAccordingToPCRModel = {
  dataQualityAssessmentTitle: string;
  dataQualityAssessmentDescriptionTooltip: string;
};

const DataQualityAssessmentAndReferenceYearsPanel = ({ epdVersionId, errors }: TProps) => {
  const [gridIsMaximized, setGridIsMaximized] = useState(false);

  const dataQualityAssessmentAndReferenceYears = useGetEpdDataQualityAssessment(epdVersionId).data;

  const createDataQualityAssessmentSummaryMutation = useCreateEpdDataQualityAssessmentSummary(epdVersionId);
  const updateDataQualityAssessmentSummaryMutation = useUpdateEpdDataQualityAssessmentSummary(epdVersionId);

  const isPcrV2 = usePcrV2();

  const labelsAccordingToPCR: LabelsAccordingToPCRModel = isPcrV2
    ? {
        dataQualityAssessmentTitle: 'Data quality assessment',
        dataQualityAssessmentDescriptionTooltip:
          'Specify the data collection period of primary data for manufacturing/service provision processes, including justifications for any deviations from using a one year data collection period.',
      }
    : {
        dataQualityAssessmentTitle: 'Data quality assessment and reference years',
        dataQualityAssessmentDescriptionTooltip:
          'In EPDs of goods, as a minimum, declare the years(s) the data of the manufacturing processes in module A3 represent. In EPDs of services, as a minimum, declare the years(s) the data of the service processes in module A5, B or C1 represent.',
      };

  const handleDataQualityAssessmentSummaryChanged = (propertyName: string, val: any) => {
    const newData = {
      ...dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentSummary,
      [propertyName]: val,
    } as DataQualityAssessmentSummaryModel;

    if (dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentSummary === null) {
      newData.epdVersionId = epdVersionId;
      createDataQualityAssessmentSummaryMutation.mutate(newData);
      return;
    }

    updateDataQualityAssessmentSummaryMutation.mutate(newData);
  };

  return (
    <>
      <MaximizedDialog
        title={labelsAccordingToPCR.dataQualityAssessmentTitle}
        visible={gridIsMaximized}
        onHide={() => setGridIsMaximized(false)}
      >
        <DataQualityAssessmentAndReferenceYearsGrid
          epdVersionId={epdVersionId}
          rows={dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentItems}
          errors={errors}
        />
      </MaximizedDialog>

      <WizardTextAreaComponent
        label="Description of data quality assessment and reference years"
        tooltip={labelsAccordingToPCR.dataQualityAssessmentDescriptionTooltip}
        placeholder="Type here"
        name="dataQualityAssessmentSummary"
        value={dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentSummary?.dataQualityAssessmentSummary}
        onChanged={handleDataQualityAssessmentSummaryChanged}
        required={isPcrV2}
        maxLength={7000}
        error={errors?.dataQualityAssessmentSummary}
      />
      <DataQualityAssessmentAndReferenceYearsGrid
        epdVersionId={epdVersionId}
        rows={dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentItems}
        onMaximize={() => setGridIsMaximized(true)}
        errors={errors}
      />
      {isPcrV2 && (
        <WizardTextAreaComponent
          label="Comment on the data sources and other information in the table"
          placeholder="Type here"
          name="dataQualityAssessmentComment"
          value={dataQualityAssessmentAndReferenceYears?.dataQualityAssessmentSummary?.dataQualityAssessmentComment}
          onChanged={handleDataQualityAssessmentSummaryChanged}
        />
      )}
    </>
  );
};

export default DataQualityAssessmentAndReferenceYearsPanel;
